import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from '../../components/toasts';
import { Button, ErrorText, Field, Modal, Select, Spinner, Textarea } from '../../components/ui';
import { readImages } from '../../lib/ai';
import { get, post } from '../../lib/api';
import { useAttendanceSettings } from '../../lib/queries';
import { AttendanceDetails, CatalogOption, ClosureContext } from '../../lib/types';
import { TicketDecisionForm } from './TicketDecisionModal';

/**
 * Finalizar: encerra para o cliente e registra no Agidesk (histórico público + registro interno
 * com o tempo efetivo). Opcionalmente encerra o chamado com classificação e FCA.
 */
export function FinishModal({ attendance, onClose, onFinished }: { attendance: AttendanceDetails; onClose: () => void; onFinished: () => void }) {
  const qc = useQueryClient();
  const [summary, setSummary] = useState('');
  const [closeTicket, setCloseTicket] = useState(false);
  const [closureId, setClosureId] = useState('');
  const [facts, setFacts] = useState<string[]>([]);
  const [causes, setCauses] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const aiFlags = useAttendanceSettings().data?.ai;
  const aiEnabled = Boolean(aiFlags?.summary);
  const aiImages = Boolean(aiFlags?.imageReading);
  const [generating, setGenerating] = useState(false);
  const [aiStep, setAiStep] = useState<string | null>(null);
  const [aiNote, setAiNote] = useState<string | null>(null);
  /** Classificação e FCA sugeridos pela IA (preenchem só os campos ainda vazios). */
  const [aiClosure, setAiClosure] = useState<{ closureId: string | null; factIds: string[]; causeIds: string[]; actionIds: string[] } | null>(null);

  const generateSummary = async () => {
    if (summary.trim() && !window.confirm('Substituir o resumo escrito pelo da IA?')) return;
    setGenerating(true);
    setError(null);
    setAiNote(null);
    try {
      // Primeiro os áudios ainda sem transcrição (mensagens de voz, gravação da ligação): o resumo sai completo.
      setAiStep('Transcrevendo os áudios…');
      const t = await post<{ transcribed: number; stillTranscribing: number; recordingPending: number }>(`/attendances/${attendance.id}/transcribe-pending`);
      const notes = [
        t.recordingPending ? 'A gravação da ligação ainda não chegou do PBX: o resumo usa só as anotações da ligação.' : null,
        t.stillTranscribing ? 'Algum áudio ainda está sendo transcrito e ficou fora do resumo.' : null,
      ].filter(Boolean);
      setAiNote(notes.length ? notes.join(' ') : null);
      if (aiImages) {
        setAiStep('Lendo as imagens…');
        await readImages(attendance.id);
      }
      setAiStep('Gerando o resumo…');
      const r = await post<{ summary: string; closure: typeof aiClosure }>(`/attendances/${attendance.id}/ai-summary`);
      setSummary(r.summary);
      setAiClosure(r.closure);
      if (r.closure) {
        const c = r.closure;
        setClosureId((v) => v || c.closureId || '');
        setFacts((v) => (v.length ? v : c.factIds));
        setCauses((v) => (v.length ? v : c.causeIds));
        setActions((v) => (v.length ? v : c.actionIds));
      }
    } catch (err) {
      setError(err);
    } finally {
      setGenerating(false);
      setAiStep(null);
    }
  };

  // Sem chamado definido (ou "decidir depois"): a primeira etapa é vincular um aberto ou abrir um novo.
  const [step, setStep] = useState<'ticket' | 'finish'>(attendance.ticketDecision === 'pending' || attendance.ticketDecision === 'deferred' ? 'ticket' : 'finish');
  const [decidedMode, setDecidedMode] = useState<string | null>(null);
  const decision = decidedMode ?? attendance.ticketDecision;
  const decided = decision !== 'pending';
  const hasTicket = decision === 'reuse' || decision === 'create';
  const ctx = useQuery({
    queryKey: ['closure-context', attendance.id],
    queryFn: () => get<ClosureContext>(`/attendances/${attendance.id}/closure-context`),
    enabled: closeTicket && step === 'finish',
  });
  const req = ctx.data?.requirements;
  const opts = ctx.data?.options;

  const finish = async () => {
    setError(null);
    if (closeTicket) {
      const missing = [
        req?.closure && !closureId && 'classificação',
        req?.fact && !facts.length && 'fato',
        req?.cause && !causes.length && 'causa',
        req?.action && !actions.length && 'ação',
      ].filter(Boolean);
      if (missing.length) return setError(new Error(`Obrigatório para encerrar o chamado: ${missing.join(', ')}`));
    }
    setSaving(true);
    try {
      await post(`/attendances/${attendance.id}/finish`, {
        summary: summary.trim() || undefined,
        closeTicket: closeTicket ? { closureId: closureId || undefined, factIds: facts, causeIds: causes, actionIds: actions } : undefined,
      });
      void qc.invalidateQueries({ queryKey: ['attendances'] });
      void qc.invalidateQueries({ queryKey: ['attendance', attendance.id] });
      toast('Atendimento finalizado. O registro está sendo enviado ao Agidesk.', 'ok');
      onFinished();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  if (step === 'ticket') {
    return (
      <Modal open title="Finalizar atendimento: chamado" onClose={onClose} width="max-w-2xl">
        <p className="mb-3 text-sm text-fg-soft">Este atendimento ainda não tem chamado. Vincule um chamado aberto do cliente ou abra um novo antes de finalizar.</p>
        <TicketDecisionForm
          attendanceId={attendance.id}
          onSaved={(mode) => {
            setDecidedMode(mode);
            setStep('finish');
          }}
          renderActions={(save, saving, ready) => (
            <div className="mt-4 flex justify-end gap-2 border-t border-border pt-3">
              <Button onClick={onClose}>Cancelar</Button>
              <Button variant="primary" onClick={save} loading={saving} disabled={!ready}>
                Continuar
              </Button>
            </div>
          )}
        />
      </Modal>
    );
  }

  return (
    <Modal
      open
      title="Finalizar atendimento"
      onClose={onClose}
      width="max-w-xl"
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => void finish()} loading={saving} disabled={!decided}>
            Finalizar
          </Button>
        </>
      }
    >
      {!decided && (
        <p className="mb-3 rounded-md bg-warn/10 px-3 py-2 text-sm text-warn">
          Defina o chamado antes de finalizar (painel ao lado, em "Chamado"). Se o Agidesk estiver fora do ar, escolha "decidir depois".
        </p>
      )}
      <Field label="Resumo" hint="(vai para o registro interno no Agidesk)">
        <Textarea rows={summary.length > 200 ? 7 : 4} value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={5000} placeholder="O que foi tratado e como ficou" />
      </Field>
      {aiEnabled && (
        <div className="-mt-2 mb-3 flex items-center gap-2">
          <Button size="sm" variant="ghost" loading={generating} onClick={() => void generateSummary()} title="A IA lê a conversa e as anotações e sugere o resumo; revise antes de finalizar">
            <Sparkles className="size-3.5" /> Gerar resumo com IA
          </Button>
          <span className="text-[11px] text-fg-soft">{aiStep ?? 'Sugestão: revise e corrija antes de finalizar.'}</span>
        </div>
      )}
      {aiNote && <p className="-mt-1 mb-3 text-xs text-warn">{aiNote}</p>}
      {aiClosure && hasTicket && !closeTicket && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-accent/5 px-3 py-2 text-xs">
          <Sparkles className="size-3.5 text-accent" />
          A IA também sugeriu a classificação e o FCA do chamado.
          <Button size="sm" variant="ghost" onClick={() => setCloseTicket(true)}>
            Encerrar o chamado com a sugestão
          </Button>
        </div>
      )}

      <label className={`flex items-center gap-2 text-sm ${hasTicket ? '' : 'opacity-50'}`}>
        <input type="checkbox" disabled={!hasTicket} checked={closeTicket} onChange={(e) => setCloseTicket(e.target.checked)} />
        Encerrar também o chamado no Agidesk
      </label>
      {!hasTicket && decided && <p className="mt-1 text-xs text-fg-soft">Disponível quando o chamado estiver definido.</p>}

      {closeTicket && (
        <div className="mt-3 rounded-md border border-border p-3">
          {ctx.isLoading && <Spinner />}
          {ctx.data && !ctx.data.agideskAvailable && <p className="mb-2 text-sm text-warn">Agidesk indisponível: o encerramento fica pendente e é feito quando ele voltar.</p>}
          {aiClosure && (
            <p className="mb-2 flex items-center gap-1.5 text-xs text-fg-soft">
              <Sparkles className="size-3.5 text-accent" /> Preenchido com a sugestão da IA: confira antes de finalizar.
            </p>
          )}
          {opts && (
            <>
              <Field label={`Classificação${req?.closure ? ' *' : ''}`}>
                <Select value={closureId} onChange={(e) => setClosureId(e.target.value)}>
                  <option value="">—</option>
                  {opts.closures.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <MultiPick label={`Fatos${req?.fact ? ' *' : ''}`} options={opts.facts} value={facts} onChange={setFacts} />
              <MultiPick label={`Causas${req?.cause ? ' *' : ''}`} options={opts.causes} value={causes} onChange={setCauses} />
              <MultiPick label={`Ações${req?.action ? ' *' : ''}`} options={opts.actions} value={actions} onChange={setActions} />
            </>
          )}
          <ErrorText error={ctx.error} />
        </div>
      )}
      <ErrorText error={error} />
    </Modal>
  );
}

function MultiPick({ label, options, value, onChange }: { label: string; options: CatalogOption[]; value: string[]; onChange: (v: string[]) => void }) {
  if (!options.length) return null;
  return (
    <Field label={label}>
      <div className="max-h-32 overflow-y-auto rounded-md border border-border p-2">
        {options.map((o) => (
          <label key={o.id} className="flex items-center gap-2 py-0.5 text-sm">
            <input type="checkbox" checked={value.includes(o.id)} onChange={(e) => onChange(e.target.checked ? [...value, o.id] : value.filter((x) => x !== o.id))} />
            {o.name}
          </label>
        ))}
      </div>
    </Field>
  );
}
